import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, ShieldCheck, BadgeCheck, Lock, EyeOff, ArrowRight, Sparkles, WashingMachine, Shirt, Wind, Truck, Clock, PiggyBank, Coins, Droplets } from "lucide-react";
import { useT } from "@/lib/i18n";
import { eur } from "@/lib/api";
import { calcStandard, loadPrices } from "./Pricing";
import { Reveal } from "@/components/Reveal";

const CITIES = [
  { name: "Berlin", plz: "10115", color: "#ff5757" },
  { name: "Hamburg", plz: "20095", color: "#7c3aed" },
  { name: "München", plz: "80331", color: "#047857" },
];

function SearchBox({ dark }) {
  const { t } = useT();
  const nav = useNavigate();
  const [plz, setPlz] = useState("");
  const valid = /^\d{5}$/.test(plz);
  return (
    <form className="flex flex-col sm:flex-row gap-3 max-w-xl" onSubmit={(e) => { e.preventDefault(); if (valid) nav(`/search?plz=${plz}`); }} data-testid="hero-search-form">
      <label className="relative flex-1">
        <span className="sr-only">{t("hero.plz")}</span>
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-2" size={20} aria-hidden="true" />
        <input className="w-input !h-14 !pl-12 !rounded-full text-lg !border-transparent shadow-lg" inputMode="numeric" maxLength={5} placeholder={t("hero.plz")} value={plz}
          onChange={(e) => setPlz(e.target.value.replace(/\D/g, ""))} data-testid="hero-plz-input" />
      </label>
      <button className={`w-btn ${dark ? "w-btn-dark" : "w-btn-primary"} !min-h-[56px]`} disabled={!valid} data-testid="hero-search-btn">
        {t("hero.cta")} <ArrowRight size={18} aria-hidden="true" />
      </button>
    </form>
  );
}

const BENEFITS = [
  [Clock, "land.b1_t", "land.b1_d", "#ffd166", "#3a2a00", "Für dich"],
  [PiggyBank, "land.b2_t", "land.b2_d", "#ff6b6b", "#3b0a0a", "Für dich"],
  [Shirt, "land.b3_t", "land.b3_d", "#7c5cff", "#ffffff", "Für dich"],
  [Coins, "land.b4_t", "land.b4_d", "#022c22", "#ffffff", "Als Waschheld"],
];
const TRUST = [["trust.local", MapPin], ["trust.kyc", BadgeCheck], ["trust.escrow", Lock], ["trust.address", EyeOff]];
const TRUST_ICON = [["#19e6b3", "#022c22"], ["#ede9fe", "#7c3aed"], ["#ffe5e5", "#c2301f"], ["#fff3d6", "#92400e"]];
const BUBBLES = [
  { Icon: WashingMachine, pos: "top-[5%] left-[3%] h-20 w-20", icon: 30, delay: "0s", anim: "w-float" },
  { Icon: Shirt, pos: "top-[19%] right-[5%] h-16 w-16", icon: 24, delay: "1.1s", anim: "w-float-slow" },
  { Icon: Droplets, pos: "top-[48%] left-[1%] h-12 w-12", icon: 20, delay: "0.5s", anim: "w-float" },
  { Icon: Wind, pos: "bottom-[18%] left-[14%] h-14 w-14", icon: 22, delay: "0.9s", anim: "w-float-slow" },
  { Icon: Truck, pos: "bottom-[12%] right-[9%] h-16 w-16", icon: 24, delay: "1.6s", anim: "w-float" },
  { Icon: Sparkles, pos: "top-[7%] left-[46%] h-10 w-10", icon: 18, delay: "2s", anim: "w-float-slow" },
  { Icon: Shirt, pos: "bottom-[42%] right-[2%] h-11 w-11", icon: 18, delay: "1.3s", anim: "w-float" },
];

const OFFERS = [
  { kg: 5, dryer: false, folding: false, ironFlat: true, transport: "none", km: 2, title: "5 kg Waschen", chips: ["Bügel-Pauschale", "Selbst bringen & abholen"] },
  { kg: 7, dryer: true, folding: true, ironFlat: false, transport: "none", km: 2, title: "7 kg Waschen", chips: ["Trockner", "Falten", "Selbst abholen"] },
  { kg: 6, dryer: false, folding: true, ironFlat: false, transport: "both", km: 2, title: "6 kg Waschen", chips: ["Falten", "Hol- & Bringservice"] },
  { kg: 8, dryer: true, folding: true, ironFlat: true, transport: "both", km: 2, title: "8 kg · Rundum sorglos", chips: ["Trockner", "Falten", "Bügel-Pauschale", "Hol & Bring"] },
  { kg: 5, dryer: false, folding: true, ironFlat: false, transport: "pickup", km: 2, title: "5 kg Waschen", chips: ["Falten", "Nur Abholung"] },
];

function RotatingOffer() {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setShow(false);
      setTimeout(() => { setI((x) => (x + 1) % OFFERS.length); setShow(true); }, 220);
    }, 3800);
    return () => clearInterval(id);
  }, []);
  const o = OFFERS[i];
  const { total } = calcStandard({ kg: o.kg, dryer: o.dryer, folding: o.folding, ironFlat: o.ironFlat, pieces: [0, 0, 0], transport: o.transport, km: o.km });
  const goto = () => {
    const p = new URLSearchParams({ kg: String(o.kg), transport: o.transport });
    if (o.dryer) p.set("dryer", "1");
    if (o.folding) p.set("folding", "1");
    if (o.ironFlat) p.set("ironFlat", "1");
    nav(`/preise?${p.toString()}`);
  };
  return (
    <button type="button" onClick={goto} data-testid="hero-offer-card" aria-label={`Angebot ansehen: ${o.title}`}
      className="absolute -bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 w-card w-hover-lift p-4 -rotate-1 text-ink text-left block cursor-pointer">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-mint-soft text-mint-ink px-2.5 py-0.5 text-[11px] font-extrabold">
          <span className="h-1.5 w-1.5 rounded-full bg-mint-ink animate-pulse" aria-hidden="true" /> Beispiel-Angebot
        </span>
        <div className="flex gap-1" aria-hidden="true">
          {OFFERS.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all duration-300 ${k === i ? "w-4 bg-mint-ink" : "w-1.5 bg-gray-300"}`} />)}
        </div>
      </div>
      <div className={`transition-opacity duration-200 ${show ? "opacity-100" : "opacity-0"}`}>
        <p className="font-extrabold text-lg leading-tight" data-testid="hero-offer-title">{o.title}</p>
        <div className="flex flex-wrap gap-1.5 my-2">
          {o.chips.map((c) => <span key={c} className="rounded-full bg-gray-100 text-ink-2 px-2 py-0.5 text-[11px] font-semibold">{c}</span>)}
        </div>
        <div className="flex items-end justify-between gap-2">
          <p className="text-2xl font-extrabold text-mint-ink" data-testid="hero-offer-price">{eur(total)}</p>
          <span className="inline-flex items-center gap-1 text-sm font-bold text-mint-ink">Zum Rechner <ArrowRight size={14} aria-hidden="true" /></span>
        </div>
      </div>
    </button>
  );
}

function CountUp({ to, suffix = "", duration = 1300 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setVal(to); return; }
    let raf, started = false;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started) {
        started = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [to, duration]);
  return <div ref={ref} data-testid="countup">{val}{suffix}</div>;
}

export default function Landing() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const [prices, setPrices] = useState(null);
  useEffect(() => { loadPrices().then(setPrices); }, []);
  const benefitTitle = (tk) => {
    if (prices && lang === "de" && tk === "land.b2_t") return `${eur(prices.base)} pro Kilo – fair & fix`;
    if (prices && lang === "de" && tk === "land.b3_t") return `Bügel-Pauschale: ${eur(prices.ironFlat)}, alles glatt`;
    return t(tk);
  };
  const marquee = prices && lang === "de"
    ? `Waschen · Trocknen · Falten · Bügeln · Abholen & Bringen · Bügel-Pauschale ${eur(prices.ironFlat)} · Feste faire Preise · Kein Weichspüler-Drama ·`
    : t("land.marquee");
  return (
    <div className="w-landing">
      <section className="relative overflow-hidden bg-mint text-mint-on">
        <div className="w-blob w-blob-a" aria-hidden="true" />
        <div className="w-blob w-blob-b" aria-hidden="true" />
        <div className="w-blob w-blob-violet" style={{ bottom: "-140px", right: "-60px" }} aria-hidden="true" />
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {BUBBLES.map((b, i) => (
            <span key={i} className={`hidden md:grid w-bubble ${b.pos} ${b.anim}`} style={{ animationDelay: b.delay }}>
              <b.Icon size={b.icon} aria-hidden="true" strokeWidth={2.2} />
            </span>
          ))}
        </div>
        <div className="w-container relative grid lg:grid-cols-12 gap-10 pt-12 md:pt-20 pb-20 md:pb-28">
          <div className="lg:col-span-7 flex flex-col justify-center w-rise">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-mint-on text-mint px-4 py-1.5 text-sm font-bold mb-6" data-testid="hero-badge"><Sparkles size={16} aria-hidden="true" /> {t("land.badge")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tight mb-6 w-headline-hero" data-testid="hero-title">{t("hero.title")}</h1>
            <p className="text-lg max-w-xl mb-8 text-mint-on opacity-80">{t("hero.sub")}</p>
            <SearchBox dark />
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="hero-cities">
              <span className="text-sm font-bold text-mint-on/70">Beliebt:</span>
              {CITIES.map((c) => (
                <button key={c.name} type="button" onClick={() => nav(`/search?plz=${c.plz}`)} data-testid={`hero-city-${c.name.toLowerCase()}`}
                  className="group inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-sm font-extrabold shadow-sm transition-transform hover:-translate-y-0.5" style={{ color: c.color }}>
                  <span className="h-2 w-2 rounded-full transition-transform group-hover:scale-125" style={{ background: c.color }} aria-hidden="true" />{c.name}
                </button>
              ))}
            </div>
            <p className="mt-4 flex items-center gap-2 text-mint-on opacity-80" data-testid="hero-search-hint">
              <MapPin size={16} aria-hidden="true" className="shrink-0" />
              <span>Von Berlin bis München – PLZ eingeben und dein Waschheld ist nur ein paar Klicks entfernt.</span>
            </p>
          </div>
          <div className="lg:col-span-5 relative w-rise" style={{ animationDelay: "120ms" }}>
            <div className="relative mt-6 lg:mt-0">
              <div className="overflow-hidden rounded-[32px] aspect-[4/5] rotate-2 w-tilt" style={{ boxShadow: "0 30px 60px -20px rgba(2,44,34,.45)" }}>
                <img src="/img/hero_washheld.jpg" alt="Waschheld faltet frische Wäsche" className="h-full w-full object-cover" />
              </div>
              <span className="absolute top-4 left-4 rounded-full bg-white/90 text-mint-ink px-3 py-1 text-xs font-extrabold shadow" data-testid="hero-badge-washheld">Dein Waschheld</span>
              <div className="absolute -top-5 -right-3 sm:-right-5 w-28 sm:w-40 overflow-hidden rounded-[22px] border-[5px] border-white rotate-[-6deg] w-float-slow" style={{ boxShadow: "0 20px 40px -14px rgba(2,44,34,.5)" }}>
                <img src="/img/hero_kunde.jpg" alt="Kundin genießt freie Zeit" className="h-32 sm:h-48 w-full object-cover" />
                <span className="absolute bottom-1.5 left-1.5 rounded-full bg-white/90 text-mint-ink px-2.5 py-0.5 text-[11px] font-extrabold shadow">Du, entspannt</span>
              </div>
              <RotatingOffer />
            </div>
          </div>
        </div>
        <svg className="block w-full text-bg" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true"><path fill="currentColor" d="M0,40 C240,90 480,0 720,30 C960,60 1200,10 1440,50 L1440,80 L0,80 Z" /></svg>
      </section>

      <div className="w-marquee border-y border-line bg-white" aria-hidden="true">
        <div className="w-marquee-track font-bold text-mint-ink uppercase tracking-wider text-sm">
          <span>{marquee} </span><span>{marquee} </span><span>{marquee} </span><span>{marquee} </span>
        </div>
      </div>

      <section className="w-container grid grid-cols-2 md:grid-cols-4 gap-4 py-14" aria-label="Vertrauen">
        {TRUST.map(([k, Icon], i) => (
          <Reveal key={k} delay={i * 80} className="w-card w-card-glow p-5 flex flex-col gap-3">
            <span className="h-12 w-12 rounded-2xl grid place-items-center shrink-0" style={{ background: TRUST_ICON[i][0], color: TRUST_ICON[i][1] }}><Icon size={22} aria-hidden="true" /></span>
            <span className="font-semibold">{t(k)}</span>
          </Reveal>
        ))}
      </section>

      <section className="w-container pb-16" aria-label="Vorteile">
        <p className="w-label !text-mint-ink">{t("land.benefits_kicker")}</p>
        <h2 className="text-3xl md:text-4xl font-extrabold mb-8 max-w-3xl w-headline">{t("land.benefits_title")}</h2>
        <div className="grid sm:grid-cols-2 gap-4 items-stretch" data-testid="landing-benefits">
          {BENEFITS.map(([Icon, tk, dk, bg, fg, tag], i) => {
            const isProvider = tag === "Als Waschheld";
            return (
              <Reveal key={tk} delay={i * 90} className="h-full">
              <div className="relative overflow-hidden rounded-[28px] p-7 md:p-9 flex flex-col h-full w-hover-lift" style={{ background: bg, color: fg }} data-testid={`benefit-${i + 1}`}>
                <div className="flex items-center justify-between gap-3 mb-5">
                  <span className="h-12 w-12 rounded-2xl grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,.3)" }}><Icon size={24} aria-hidden="true" /></span>
                  <span className="rounded-full text-[11px] font-extrabold px-3 py-1 tracking-wide" style={{ background: "rgba(255,255,255,.25)" }}>{tag}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold leading-tight mb-2">{benefitTitle(tk)}</h3>
                <p className="text-base md:text-lg opacity-85 max-w-md">{t(dk)}</p>
                {isProvider && <Link to="/waschheld" className="inline-flex items-center gap-2 mt-auto pt-6 font-bold underline underline-offset-4" data-testid="benefit-hero-link">Waschheld werden <ArrowRight size={18} aria-hidden="true" /></Link>}
                <Icon className="absolute -right-6 -bottom-6 opacity-10 pointer-events-none" size={160} aria-hidden="true" />
              </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="w-container pb-16">
        <div className="rounded-[32px] bg-mint-soft p-8 md:p-14 grid md:grid-cols-12 gap-10 w-grain-mint">
          <div className="md:col-span-4">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 w-headline">{t("how.title")}</h2>
            <div className="flex gap-6 text-mint-ink font-extrabold text-3xl tabular-nums">
              <div><CountUp to={100} suffix=" %" /><div className="text-sm font-semibold text-ink-2">{t("land.stat1")}</div></div>
              <div><CountUp to={2} suffix=" Min" /><div className="text-sm font-semibold text-ink-2">{t("land.stat2")}</div></div>
            </div>
          </div>
          <ol className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((n) => (
              <Reveal as="li" key={n} delay={(n - 1) * 90} className="w-card w-hover-lift p-5 flex gap-4 items-start">
                <span className="h-10 w-10 shrink-0 rounded-full grid place-items-center bg-mint text-mint-on font-extrabold tabular-nums">{n}</span>
                <span className="text-base pt-2">{t(`how.${n}`)}</span>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-container py-6 md:py-12 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative">
          <div className="absolute -inset-3 rounded-[32px] bg-mint rotate-[-3deg]" aria-hidden="true" />
          <img src="/img/folded.jpg" alt="Frisch gefaltete Wäsche" className="relative rounded-[24px] w-full aspect-[3/2] object-cover" />
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 w-headline">{t("land.provider_title")}</h2>
          <p className="w-muted mb-6 text-lg">{t("land.provider_sub")}</p>
          <button className="w-btn w-btn-primary" onClick={() => nav("/waschheld")} data-testid="landing-become-provider-btn">{t("nav.become")} <ArrowRight size={18} aria-hidden="true" /></button>
        </div>
      </section>

      <section className="w-container py-16">
        <div className="relative overflow-hidden rounded-[32px] text-white p-8 md:p-14 w-noise" style={{ background: "linear-gradient(130deg, #022c22 0%, #053d2f 42%, #2a1055 100%)" }}>
          <div className="w-blob w-blob-c" aria-hidden="true" />
          <div className="w-blob w-blob-violet" style={{ bottom: "-150px", right: "-60px" }} aria-hidden="true" />
          <div className="w-blob w-blob-coral" style={{ top: "-130px", left: "38%" }} aria-hidden="true" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-3 w-headline-light">{t("land.cta_title")}</h2>
              <p className="text-white/75 text-lg">{t("land.cta_sub")}</p>
            </div>
            <SearchBox />
          </div>
        </div>
      </section>

    </div>
  );
}
